import React, { useState } from 'react'
import {
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Tabs,
  Tab,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material'
import {
  Close,
  Shield,
  Inbox,
  CardGiftcard,
  People,
  Autorenew,
  Assessment,
  FactCheck,
  Tune,
  TableChart,
  ExpandMore,
  CheckCircle
} from '@mui/icons-material'

import ProductInfoTab from './components/Products/Product.js'
import PackagesTab from './components/Packages/PackagesTab.js'
import AddonsTab from './components/AddOns/AddonsTab.js'
import CashbackTab from './components/Cashback/CashbackTab.js'
import AgeRulesTab from './components/AgeRules/AgeRulesTab.js'
import AutoRenewalTab from './components/AutoRenewal/AutoRenewalTab.js'
import FactsGovernanceTab from './components/Governance/FactsGovernanceTab.js'
import AllocationSettingsTab from './components/Governance/AllocationSettingsTab.js'
import RatingTablesReview from './components/RatingTables/RatingTablesReview.js'

/**
 * Full read-only review of an AI-drafted product before approving the import.
 * Reuses the ViewProduct tab components (no edit/add/delete handlers passed → they
 * render read-only). The connected ProductInfoTab reads `currentProduct`, which the
 * reducer populates from the draft on REVIEW_DRAFT_SUCCESS; sub-entity tabs read the
 * draft object passed here as props. "Approve & Import" deep-imports the full graph.
 */
export function ReviewDraftProduct({
  open,
  reviewDraft,
  currentProduct,
  importDraftLoader,
  importDraftError,
  importedDraftProduct,
  onImport,
  onClose
}) {
  const [activeTab, setActiveTab] = useState(0)

  const draftProduct =
    (reviewDraft && reviewDraft.result && reviewDraft.result.draft_product) ||
    currentProduct ||
    {}
  const result = (reviewDraft && reviewDraft.result) || {}
  const jobId = reviewDraft && reviewDraft.job_id
  const alreadyImported =
    (reviewDraft && reviewDraft.imported) || !!importedDraftProduct

  // Drafts never carry concrete rating nodes — the drafter emits opaque
  // pricing_hints[] instead (see result.pricing_hints below) and rating nodes
  // are configured manually after import. Pass [] for prop-shape parity with
  // the real ViewProduct tabs, which expect a ratingNodes array.
  const draftRatingNodes = draftProduct.rating_nodes || []

  // Collapsible section, matching the ViewProduct Overview accordions.
  const foldSection = (icon, title, subtitle, node) => (
    <Accordion
      disableGutters
      sx={{ border: '1px solid #e5e7eb', borderRadius: '8px !important', mb: 1, '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {icon}
          <Box>
            <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
            <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>{node}</AccordionDetails>
    </Accordion>
  )

  // Mirrors ViewProduct's 5-tab layout, fed the draft data. No edit/save
  // handlers are wired, so the reused editor components render layout-only.
  const renderTab = () => {
    switch (activeTab) {
      case 0:
        return (
          <Box>
            <ProductInfoTab currentProduct={draftProduct} readOnly />
            <Box sx={{ mt: 4 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6B7280', mb: 1.5 }}>
                Product-level configuration
              </Typography>
              {foldSection(<CardGiftcard sx={{ color: '#173E4B' }} />, 'Cashback', 'Loyalty / cashback configuration', <CashbackTab cashback={draftProduct.cashback} readOnly />)}
              {foldSection(<People sx={{ color: '#173E4B' }} />, 'Age Rules', 'Age validation rules', <AgeRulesTab ageRules={draftProduct.age_validation_rules} readOnly />)}
              {foldSection(<Autorenew sx={{ color: '#173E4B' }} />, 'Auto-Renewal', 'Renewal configuration', <AutoRenewalTab autoRenewal={draftProduct.renewal} readOnly />)}
            </Box>
          </Box>
        )
      case 1:
        return (
          <PackagesTab
            section="packages"
            packages={draftProduct.packages}
            ratingNodes={draftRatingNodes}
            currency={draftProduct.currency}
            readOnly
          />
        )
      case 2:
        return (
          <Box>
            <PackagesTab
              section="benefits"
              packages={draftProduct.packages}
              ratingNodes={draftRatingNodes}
              currency={draftProduct.currency}
              readOnly
            />
            <Divider sx={{ my: 4 }} />
            <AddonsTab
              addons={draftProduct.add_ons}
              ratingNodes={draftRatingNodes}
              currency={draftProduct.currency}
              readOnly
            />
          </Box>
        )
      case 3:
        return <RatingTablesReview ratingTables={draftProduct.rating_tables} />
      case 4:
        return <FactsGovernanceTab product={draftProduct} />
      case 5:
        return <AllocationSettingsTab product={draftProduct} />
      default:
        return null
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar sx={{ position: 'relative', backgroundColor: '#173E4B' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose}>
            <Close />
          </IconButton>
          <Box sx={{ ml: 2, flexGrow: 1 }}>
            <Typography variant="h6">
              Review drafted product: {draftProduct.name || '—'}
            </Typography>
            <Typography variant="caption">
              Review every section generated from the document before importing.
            </Typography>
          </Box>
          {typeof result.confidence === 'number' && (
            <Chip
              sx={{ mr: 2, color: '#fff', borderColor: '#fff' }}
              variant="outlined"
              label={`Confidence ${(result.confidence * 100).toFixed(0)}%`}
            />
          )}
          {alreadyImported ? (
            <Chip icon={<CheckCircle />} color="success" label="Imported" />
          ) : (
            <Button
              color="inherit"
              variant="outlined"
              disabled={importDraftLoader || !jobId}
              onClick={() => onImport(jobId)}
            >
              {importDraftLoader ? (
                <CircularProgress size={22} sx={{ color: '#fff' }} />
              ) : (
                'Approve & Import'
              )}
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {importDraftError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {importDraftError}
          </Alert>
        )}
        {importedDraftProduct && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Imported as product{' '}
            {importedDraftProduct.name || importedDraftProduct.guid}. You can
            now close this view and find it in the products list.
          </Alert>
        )}

        {result.pricing_hints && result.pricing_hints.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              Pricing hints (not statically encoded — configure rating nodes for
              these after import)
            </Typography>
            {result.pricing_hints.map((h, i) => (
              <Typography key={i} variant="body2">
                •{' '}
                {typeof h === 'string'
                  ? h
                  : `${h.condition || JSON.stringify(h)}${
                      h.schema_path ? ` (${h.schema_path})` : ''
                    }`}
              </Typography>
            ))}
          </Alert>
        )}
        {result.open_questions && result.open_questions.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Open questions</Typography>
            {result.open_questions.map((q, i) => (
              <Typography key={i} variant="body2">
                • {typeof q === 'string' ? q : q.question || JSON.stringify(q)}
              </Typography>
            ))}
          </Alert>
        )}
        {result.excluded && result.excluded.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              Excluded (not modelled — review the source document)
            </Typography>
            {result.excluded.map((e, i) => (
              <Typography key={i} variant="body2">
                •{' '}
                {typeof e === 'string'
                  ? e
                  : e.reason || e.source_excerpt || JSON.stringify(e)}
              </Typography>
            ))}
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Shield />} iconPosition="start" label="Overview" />
          <Tab icon={<Inbox />} iconPosition="start" label="Packages" />
          <Tab icon={<Assessment />} iconPosition="start" label="Benefits & Rating" />
          <Tab icon={<TableChart />} iconPosition="start" label="Rating Tables" />
          <Tab icon={<FactCheck />} iconPosition="start" label="Facts & Governance" />
          <Tab icon={<Tune />} iconPosition="start" label="Allocation & Settings" />
        </Tabs>
        <Box sx={{ mt: 2 }}>{renderTab()}</Box>
      </Box>
    </Dialog>
  )
}
